<template>
    <div class="catalog">
        <aside class="catalog-list">
            <div class="search">
                <ChatIcon name="search" :size="15" class="search-icon" />
                <input
                    v-model="search"
                    type="text"
                    class="search-input"
                    placeholder="Найти таблицу…"
                >
            </div>

            <div class="list-scroll">
                <div v-if="loading" class="list-state">
                    <span class="spinner" />
                    Загружаем список…
                </div>
                <div v-else-if="error" class="list-state list-state--error">{{ error }}</div>
                <div v-else-if="planExpired && !reports.length" class="list-state list-state--error">
                    Срок тарифа истёк — индивидуальные таблицы недоступны. Продлите договор, чтобы вернуть доступ.
                </div>
                <div v-else-if="!reports.length" class="list-state">
                    Пока нет доступных таблиц. Они появятся по вашему тарифу.
                </div>
                <div v-else-if="!filteredGroups.length" class="list-state">
                    По запросу ничего не найдено.
                </div>

                <div v-else class="groups">
                    <div v-for="group in filteredGroups" :key="group.category" class="group">
                        <div class="group-title">{{ group.category }}</div>
                        <button
                            v-for="report in group.items"
                            :key="report.id"
                            type="button"
                            class="report-btn"
                            :class="{ 'report-btn--active': selected && selected.id === report.id }"
                            @click="selectReport(report)"
                        >
                            <span class="report-btn-icon">
                                <ChatIcon name="columns" :size="15" />
                            </span>
                            <span class="report-btn-body">
                                <span class="report-btn-title">{{ report.title }}</span>
                                <span v-if="report.columns_count" class="report-btn-meta">
                                    {{ report.columns_count }} {{ columnsWord(report.columns_count) }}
                                </span>
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </aside>

        <section class="catalog-main">
            <div v-if="!selected" class="empty">
                <span class="empty-icon">
                    <ChatIcon name="database-search" :size="28" />
                </span>
                <p class="empty-title">Выберите таблицу</p>
                <p class="empty-text">
                    Слева — список готовых таблиц по вашему тарифу. Откройте любую,
                    чтобы посмотреть предпросмотр и скачать полные данные в Excel или CSV.
                </p>
            </div>

            <template v-else>
                <header class="main-head">
                    <div class="main-head-text">
                        <h2 class="main-title">{{ selected.title }}</h2>
                        <div class="tags">
                            <span class="tag tag--cat">{{ selected.category || 'Отчёты' }}</span>
                            <span v-if="selected.columns_count" class="tag">
                                {{ selected.columns_count }} {{ columnsWord(selected.columns_count) }}
                            </span>
                            <span class="tag tag--fmt">Excel</span>
                            <span class="tag tag--fmt">CSV</span>
                            <span v-if="selected.has_params" class="tag tag--param">с параметрами</span>
                        </div>
                    </div>
                    <div v-if="!planExpired" class="main-actions">
                        <button
                            type="button"
                            class="btn btn--primary"
                            :disabled="!!exporting"
                            @click="runExport('xlsx')"
                        >
                            <span v-if="exporting === 'xlsx'" class="spinner spinner--light" />
                            <ChatIcon v-else name="download" :size="15" />
                            <span>{{ exporting === 'xlsx' ? exportStatusLabel : 'Скачать Excel' }}</span>
                        </button>
                        <button
                            type="button"
                            class="btn"
                            :disabled="!!exporting"
                            @click="runExport('csv')"
                        >
                            <span v-if="exporting === 'csv'" class="spinner" />
                            <ChatIcon v-else name="download" :size="15" />
                            <span>{{ exporting === 'csv' ? exportStatusLabel : 'Скачать CSV' }}</span>
                        </button>
                    </div>
                </header>

                <p class="main-desc">
                    {{ selected.description || 'Готовая таблица с данными по вашему тарифу. Ниже — предпросмотр первых строк; полные данные доступны в выгрузке Excel или CSV.' }}
                </p>

                <!-- Тариф истёк: показываем отчёт, но доступ закрыт до продления. -->
                <div v-if="planExpired" class="locked-card">
                    <span class="locked-icon">
                        <ChatIcon name="lock" :size="26" />
                    </span>
                    <p class="locked-title">Доступ к отчёту приостановлен</p>
                    <p class="locked-text">
                        Срок вашего тарифа истёк. Продлите тариф, чтобы снова открывать
                        предпросмотр и скачивать полные данные в Excel и CSV.
                    </p>
                    <button type="button" class="btn btn--primary" @click="$emit('upgrade')">
                        Продлить тариф
                    </button>
                </div>

                <template v-else>
                <p v-if="exportError" class="export-error">
                    <ChatIcon name="alert" :size="14" />
                    <span>{{ exportError }}</span>
                </p>

                <div class="preview-card">
                    <div class="preview-bar">
                        <ChatIcon name="eye" :size="14" />
                        <span v-if="previewLoading">Строим предпросмотр…</span>
                        <span v-else-if="previewError">Предпросмотр недоступен</span>
                        <span v-else-if="!preview.rows.length">Нет данных для отображения</span>
                        <span v-else>Предпросмотр · первые {{ preview.rows.length }} строк</span>
                    </div>

                    <div v-if="previewLoading" class="preview-state">
                        <span class="spinner" />
                        Загружаем данные…
                    </div>
                    <div v-else-if="previewError" class="preview-state preview-state--error">
                        {{ previewError }}
                    </div>
                    <div v-else-if="!preview.rows.length" class="preview-state">
                        В этой таблице сейчас нет строк.
                    </div>
                    <div v-else class="table-wrap">
                        <table class="table">
                            <thead>
                                <tr>
                                    <th class="idx-col">#</th>
                                    <th v-for="col in preview.columns" :key="col.key">{{ col.label }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(row, i) in preview.rows" :key="i">
                                    <td class="idx-col">{{ i + 1 }}</td>
                                    <td v-for="col in preview.columns" :key="col.key">{{ row[col.key] }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <p v-if="!previewLoading && !previewError && preview.rows.length" class="preview-note">
                        Это только предпросмотр. Полные данные — в выгрузке Excel или CSV.
                    </p>
                </div>
                </template>
            </template>
        </section>
    </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import store from '@/store'
import ChatIcon from '@/components/chat/icons/ChatIcon.vue'
import { getEcho } from '@/utils/echo'
import {
    listReports,
    previewReport,
    enqueueReportRun,
    listReportRuns,
    getReportRunDownloadUrl,
} from '@/utils/reportsApi'

defineEmits(['upgrade'])

const reports = ref([])
const planExpired = ref(false)
const loading = ref(true)
const error = ref('')

const selected = ref(null)
const preview = ref({ columns: [], rows: [] })
const previewLoading = ref(false)
const previewError = ref('')

const search = ref('')

const exporting = ref('')
const exportStatus = ref('')
const exportError = ref('')

const exportStatusLabel = computed(() => {
    if (exportStatus.value === 'queued') return 'В очереди…'
    if (exportStatus.value === 'running') return 'Готовим файл…'
    return 'Обработка…'
})

const filteredGroups = computed(() => {
    const q = search.value.trim().toLowerCase()
    const map = new Map()
    for (const report of reports.value) {
        if (q && !report.title.toLowerCase().includes(q)) continue
        const category = report.category || 'Отчёты'
        if (!map.has(category)) map.set(category, [])
        map.get(category).push(report)
    }
    return [...map.entries()].map(([category, items]) => ({ category, items }))
})

// WS-подписка на статусы выгрузок: без опроса, пуш прилетает из
// GenerateReportRunJob (событие ReportRunUpdated, канал reports.user.{id}).
const pending = new Map()
let echoChannel = null
let echoUserId = null

function resolveUserId() {
    return store?.state?.main?.userInfo?.id || store?.state?.userInfo?.id || null
}

function subscribeRuns() {
    const userId = resolveUserId()
    if (!userId || echoUserId === userId) return
    teardownRuns()
    try {
        const echo = getEcho()
        echoChannel = echo.private(`reports.user.${userId}`)
        // Точка перед именем — событие с broadcastAs(), без App\Events неймспейса.
        echoChannel.listen('.report.run.updated', (payload) => {
            if (exporting.value) exportStatus.value = payload.status
            if (payload.status === 'done' || payload.status === 'failed') {
                const resolve = pending.get(payload.run_id)
                if (resolve) {
                    pending.delete(payload.run_id)
                    resolve(payload)
                }
            }
        })
        echoUserId = userId
    } catch {
        // WS недоступен — сработает разовый REST-фолбэк в waitForRun.
    }
}

function teardownRuns() {
    if (echoChannel && echoUserId) {
        try {
            getEcho().leave(`reports.user.${echoUserId}`)
        } catch {
            // noop
        }
    }
    echoChannel = null
    echoUserId = null
}

onMounted(async () => {
    subscribeRuns()
    loading.value = true
    error.value = ''
    try {
        const { items, planExpired: expired } = await listReports()
        reports.value = items
        planExpired.value = expired
    } catch (e) {
        error.value = e?.message || 'Не удалось загрузить отчёты.'
    } finally {
        loading.value = false
    }
})

onUnmounted(teardownRuns)

async function selectReport(report) {
    selected.value = report
    exportError.value = ''
    previewError.value = ''
    preview.value = { columns: [], rows: [] }
    // Тариф истёк — не запрашиваем предпросмотр (бэк вернёт 403), показываем
    // блок «продлите тариф».
    if (planExpired.value) {
        previewLoading.value = false
        return
    }
    previewLoading.value = true
    try {
        const data = await previewReport(report.id, { limit: 50 })
        preview.value = { columns: data.columns || [], rows: data.rows || [] }
    } catch (e) {
        previewError.value = e?.message || 'Не удалось построить предпросмотр.'
    } finally {
        previewLoading.value = false
    }
}

async function runExport(format) {
    if (exporting.value || !selected.value) return
    exporting.value = format
    exportError.value = ''
    exportStatus.value = 'queued'
    subscribeRuns()
    try {
        const { run_id: runId } = await enqueueReportRun(selected.value.id, { format })
        const run = await waitForRun(runId)
        if (run.status === 'done') {
            const { url } = await getReportRunDownloadUrl(runId)
            if (url) triggerDownload(url)
        } else {
            exportError.value = run.error_message || 'Не удалось сформировать файл.'
        }
    } catch (e) {
        exportError.value = e?.message || 'Ошибка при формировании выгрузки.'
    } finally {
        exporting.value = ''
        exportStatus.value = ''
    }
}

/**
 * Ждём завершения выгрузки по WebSocket. Если пуш не долетел за timeout
 * (WS лёг / прокси) — один разовый REST-запрос статуса как страховка.
 */
function waitForRun(runId, { timeoutMs = 180000 } = {}) {
    return new Promise((resolve) => {
        let settled = false
        const finish = (payload) => {
            if (settled) return
            settled = true
            clearTimeout(timer)
            pending.delete(runId)
            resolve(payload)
        }
        pending.set(runId, finish)
        const timer = setTimeout(async () => {
            try {
                const runs = await listReportRuns({ reportId: selected.value?.id })
                const run = runs.find((r) => r.id === runId)
                finish(run || { status: 'failed', error_message: 'Превышено время ожидания.' })
            } catch {
                finish({ status: 'failed', error_message: 'Превышено время ожидания.' })
            }
        }, timeoutMs)
    })
}

function columnsWord(n) {
    const mod10 = n % 10
    const mod100 = n % 100
    if (mod10 === 1 && mod100 !== 11) return 'столбец'
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'столбца'
    return 'столбцов'
}

function triggerDownload(url) {
    const a = document.createElement('a')
    a.href = url
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
}
</script>

<style scoped>
.catalog {
    display: grid;
    grid-template-columns: 300px 1fr;
    gap: 16px;
    width: 100%;
    height: 100%;
    min-height: 0;
}

.catalog-list,
.catalog-main {
    background: #fff;
    border: 1px solid #e7e7e7;
    border-radius: 12px;
    min-height: 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    box-shadow: 0 1px 2px rgba(21, 26, 34, 0.03);
}

/* --- Left list --- */
.catalog-list {
    padding: 12px 10px;
}

.search {
    position: relative;
    margin-bottom: 10px;
    flex-shrink: 0;
}

.search-icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #9a9a9a;
    pointer-events: none;
}

.search-input {
    width: 100%;
    padding: 9px 10px 9px 32px;
    border: 1px solid #e0e0e0;
    border-radius: 9px;
    font-size: 13px;
    font-family: inherit;
    box-sizing: border-box;
    background: #fafafa;
    transition: border-color 0.12s ease, background 0.12s ease;
}

.search-input:focus {
    outline: none;
    border-color: #198FA0;
    background: #fff;
}

.list-scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    margin: 0 -2px;
    padding: 0 2px;
}

.groups {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.group-title {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: #9a9a9a;
    padding: 0 8px 5px;
}

.report-btn {
    display: flex;
    align-items: center;
    gap: 9px;
    width: 100%;
    text-align: left;
    padding: 8px 9px;
    border: 0;
    border-radius: 9px;
    background: transparent;
    font-family: inherit;
    color: #23282C;
    cursor: pointer;
    transition: background 0.12s ease;
}

.report-btn:hover {
    background: #f4f4f4;
}

.report-btn--active {
    background: rgba(25, 143, 160, 0.1);
}

.report-btn-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: #eef1f2;
    color: #6b7276;
    flex-shrink: 0;
}

.report-btn--active .report-btn-icon {
    background: rgba(25, 143, 160, 0.16);
    color: #14707e;
}

.report-btn-body {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
}

.report-btn-title {
    display: block;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.report-btn--active .report-btn-title {
    color: #14707e;
    font-weight: 600;
}

.report-btn-meta {
    font-size: 10.5px;
    color: #a0a0a0;
}

.list-state {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px 8px;
    font-size: 12.5px;
    color: #888888;
    line-height: 1.5;
}

.list-state--error {
    color: #B83232;
}

/* --- Right main --- */
.catalog-main {
    padding: 18px 20px;
}

.empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 24px;
}

.empty-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 60px;
    height: 60px;
    border-radius: 16px;
    background: #eef4f5;
    color: #198FA0;
    margin-bottom: 14px;
}

.empty-title {
    margin: 0 0 6px;
    font-size: 16px;
    font-weight: 700;
    color: #23282C;
}

.empty-text {
    margin: 0;
    max-width: 380px;
    font-size: 13px;
    line-height: 1.55;
    color: #888888;
}

.locked-card {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 6px;
    padding: 40px 28px;
    background: #fff;
    border: 1px dashed #e0c68a;
    border-radius: 12px;
}

.locked-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 15px;
    background: rgba(214, 158, 46, 0.14);
    color: #b0791a;
    margin-bottom: 4px;
}

.locked-title {
    margin: 6px 0 0;
    font-size: 16px;
    font-weight: 700;
    color: #23282C;
}

.locked-text {
    margin: 0 0 10px;
    max-width: 420px;
    font-size: 13px;
    line-height: 1.55;
    color: #7c8285;
}

.main-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    flex-wrap: wrap;
    flex-shrink: 0;
}

.main-head-text {
    min-width: 0;
}

.main-title {
    font-size: 17px;
    font-weight: 700;
    color: #23282C;
    margin: 0;
    word-break: break-word;
}

.tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin: 8px 0 0;
}

.tag {
    font-size: 10.5px;
    font-weight: 600;
    color: #5f5f5f;
    background: #f0f0f0;
    border-radius: 8px;
    padding: 3px 8px;
    white-space: nowrap;
}

.tag--cat {
    color: #14707e;
    background: rgba(25, 143, 160, 0.12);
}

.tag--fmt {
    color: #2f7d32;
    background: rgba(47, 125, 50, 0.1);
}

.tag--param {
    color: #8a5a00;
    background: rgba(214, 158, 46, 0.14);
}

.main-actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
}

.btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 9px 14px;
    border: 1px solid #d5d5d5;
    border-radius: 9px;
    background: #fff;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    color: #23282C;
    cursor: pointer;
    min-width: 140px;
    transition: background 0.12s ease, border-color 0.12s ease;
}

.btn:hover:not(:disabled) {
    background: #f4f4f4;
}

.btn--primary {
    background: #198FA0;
    border-color: #198FA0;
    color: #fff;
}

.btn--primary:hover:not(:disabled) {
    background: #14707e;
    border-color: #14707e;
}

.btn:disabled {
    opacity: 0.7;
    cursor: default;
}

.main-desc {
    margin: 12px 0 0;
    font-size: 13px;
    line-height: 1.5;
    color: #7c8285;
    flex-shrink: 0;
}

.export-error {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 12px 0 0;
    color: #B83232;
    font-size: 12.5px;
    flex-shrink: 0;
}

.preview-card {
    margin-top: 14px;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    border: 1px solid #ececec;
    border-radius: 10px;
    overflow: hidden;
}

.preview-bar {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 9px 12px;
    background: #fafafa;
    border-bottom: 1px solid #ececec;
    font-size: 12px;
    font-weight: 600;
    color: #6b7276;
    flex-shrink: 0;
}

.table-wrap {
    flex: 1;
    min-height: 0;
    overflow: auto;
}

.table {
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
    font-size: 12.5px;
}

.table th,
.table td {
    border-bottom: 1px solid #eee;
    padding: 8px 12px;
    text-align: left;
    white-space: nowrap;
}

.table th {
    background: #f7f8f9;
    font-weight: 600;
    color: #5f5f5f;
    position: sticky;
    top: 0;
    z-index: 1;
    box-shadow: inset 0 -1px 0 #e7e7e7;
}

.idx-col {
    color: #b0b0b0;
    text-align: right;
    font-variant-numeric: tabular-nums;
    width: 1%;
}

.table tbody tr:nth-child(even) td {
    background: #fbfbfb;
}

.table tbody tr:hover td {
    background: #f2f7f8;
}

.preview-state {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 28px 16px;
    font-size: 13px;
    color: #888888;
}

.preview-state--error {
    color: #B83232;
}

.preview-note {
    margin: 0;
    padding: 8px 12px;
    font-size: 11.5px;
    color: #a5a5a5;
    border-top: 1px solid #f0f0f0;
    background: #fff;
    flex-shrink: 0;
}

/* --- Spinner --- */
.spinner {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 2px solid rgba(25, 143, 160, 0.25);
    border-top-color: #198FA0;
    animation: spin 0.7s linear infinite;
    flex-shrink: 0;
}

.spinner--light {
    border-color: rgba(255, 255, 255, 0.4);
    border-top-color: #fff;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

@media (max-width: 860px) {
    .catalog {
        grid-template-columns: 1fr;
        height: auto;
    }

    .catalog-list {
        max-height: 260px;
    }
}
</style>
